import { Fragment, useState } from 'react';
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, RotateCcw } from 'lucide-react';
import type { EnvironmentClassification, ExceptionView, ServiceView } from '@vwt/contracts';
import { Badge, Button, Card, Checkbox, EmptyState, Input, NativeSelect, toast } from '@vwt/ui';
import { api, newIdempotencyKey, POLL_ACTIVE, useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { formatDuration, formatNumber } from '../../lib/format.js';
import { useTranslation } from '../../lib/i18n.js';
import { QueryState, TimeAgo } from '../../components/common.js';
import { ReasonDialog } from '../../components/reason-dialog.js';
import { RepositoryDialog } from '../../components/repository-dialog.js';

const healthTone = { HEALTHY: 'success', DEGRADED: 'warning', FAILING: 'danger', DISABLED: 'outline', UNVERIFIED: 'neutral' } as const;

export function IntegrationsPanel() {
  const { t } = useTranslation();
  const list = useApiQuery('listIntegrations', {}, { refetchInterval: POLL_ACTIVE, retry: false });
  const reconcile = useApiMutation('reconcileIntegration', { invalidate: ['listIntegrations'] });
  const probe = useApiMutation('probeIntegration', { invalidate: ['listIntegrations'] });
  const [probing, setProbing] = useState<string | null>(null);
  return (
    <QueryState query={list}>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {(list.data?.data ?? []).map((c) => (
          <Card key={c.id} className="p-4" data-testid="integration-card">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold">{c.displayName}</p>
              <Badge tone={c.mode === 'live' ? 'accent' : c.mode === 'fixture' ? 'warning' : 'outline'}>{t(`admin.integ.modes.${c.mode}`)}</Badge>
              <Badge tone={healthTone[c.health]}>{t(`admin.integ.health.${c.health}`)}</Badge>
              {c.liveVerifiedAt ? <Badge tone="success">{t('admin.integ.liveVerified')}</Badge> : null}
            </div>
            <dl className="mt-2 grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-1 text-xs">
              <dt className="text-fg-subtle">{t('admin.integ.lastSuccess')}</dt>
              <dd>
                <TimeAgo iso={c.lastSuccessAt} />
              </dd>
              <dt className="text-fg-subtle">{t('admin.integ.lastAttempt')}</dt>
              <dd>
                <TimeAgo iso={c.lastAttemptAt} />
              </dd>
              {c.lastSafeError ? (
                <>
                  <dt className="text-fg-subtle">{t('admin.integ.lastError')}</dt>
                  <dd className="text-danger">{c.lastSafeError}</dd>
                </>
              ) : null}
              {c.circuitOpenUntil ? (
                <>
                  <dt className="text-fg-subtle">{t('admin.integ.circuit')}</dt>
                  <dd>
                    <TimeAgo iso={c.circuitOpenUntil} />
                  </dd>
                </>
              ) : null}
              {c.cursors.map((cur) => (
                <Fragment key={cur.name}>
                  <dt className="truncate text-fg-subtle" title={cur.name}>
                    {t('admin.integ.cursor')} {cur.name}
                  </dt>
                  <dd className="truncate font-mono">{cur.position ?? '—'}</dd>
                </Fragment>
              ))}
            </dl>
            {c.warnings.length ? (
              <ul className="mt-2 flex flex-col gap-1">
                {c.warnings.map((w) => (
                  <li key={w} className="rounded bg-warning-soft px-2 py-1 text-xs text-warning">
                    {w}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                loading={probing === c.id}
                onClick={async () => {
                  setProbing(c.id);
                  await probe.mutateAsync({ params: { id: c.id } }).catch(toastError);
                  setProbing(null);
                }}
              >
                <Activity /> {t('admin.integ.probe')}
              </Button>
              {c.provider === 'GITHUB' || c.provider === 'C4C7US' || c.provider === 'VAMMO_APPS' ? (
                <Button size="sm" onClick={() => void reconcile.mutateAsync({ params: { id: c.id }, idempotencyKey: newIdempotencyKey() }).then(() => toast.success(t('admin.integ.reconcileQueued'))).catch(toastError)}>
                  <RefreshCw /> {t('admin.integ.reconcile')}
                </Button>
              ) : null}
            </div>
          </Card>
        ))}
      </div>
    </QueryState>
  );
}

export const SLACK_MILESTONES = ['REQUEST_RECEIVED', 'REQUEST_ACCEPTED', 'REQUEST_REJECTED', 'REQUEST_RESOLVED', 'REQUEST_NEEDS_ATTENTION', 'WORK_CREATED', 'WORK_STARTED', 'WORK_IN_REVIEW', 'WORK_BLOCKED', 'WORK_INTEGRATED', 'WORK_DEPLOYED', 'WORK_DELIVERED', 'WORK_CANCELED'] as const;
const MILESTONES = SLACK_MILESTONES;

/** Approved Slack destinations per project or team (Section 10.1). */
export function SlackDestinationsPanel() {
  const { t } = useTranslation();
  const list = useApiQuery('listSlackDestinations', {}, { retry: false });
  const projects = useApiQuery('listProjects', { query: {} });
  const teams = useApiQuery('listTeams', { query: {} });
  const save = useApiMutation('setSlackDestination', { invalidate: ['listSlackDestinations'] });
  const remove = useApiMutation('deleteSlackDestination', { invalidate: ['listSlackDestinations'] });
  const [form, setForm] = useState({ scopeKind: 'PROJECT' as 'PROJECT' | 'TEAM', scopeId: '', workspaceId: '', channelId: '', approvedForRestricted: false, milestones: ['REQUEST_ACCEPTED', 'REQUEST_RESOLVED', 'WORK_DELIVERED'] as string[] });
  const scopes = form.scopeKind === 'PROJECT' ? (projects.data?.data ?? []).map((p) => ({ id: p.id, label: `${p.key} · ${p.name}` })) : (teams.data?.data ?? []).map((tm) => ({ id: tm.id, label: tm.name }));
  const labelOf = (kind: string, id: string) => (kind === 'PROJECT' ? (projects.data?.data ?? []).find((p) => p.id === id)?.key : (teams.data?.data ?? []).find((tm) => tm.id === id)?.name) ?? id.slice(0, 8);
  return (
    <section className="mt-6 flex flex-col gap-3">
      <h2 className="text-sm font-semibold">{t('admin.slackDest.title')}</h2>
      <p className="text-sm text-fg-muted">{t('admin.slackDest.help')}</p>
      <Card className="flex flex-col gap-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <NativeSelect value={form.scopeKind} onChange={(e) => setForm((f) => ({ ...f, scopeKind: e.target.value as 'PROJECT' | 'TEAM', scopeId: '' }))} aria-label={t('admin.slackDest.scope')}>
            <option value="PROJECT">{t('admin.slackDest.project')}</option>
            <option value="TEAM">{t('admin.slackDest.team')}</option>
          </NativeSelect>
          <NativeSelect value={form.scopeId} onChange={(e) => setForm((f) => ({ ...f, scopeId: e.target.value }))} aria-label={form.scopeKind === 'PROJECT' ? t('admin.slackDest.project') : t('admin.slackDest.team')}>
            <option value="">—</option>
            {scopes.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </NativeSelect>
          <Input value={form.workspaceId} onChange={(e) => setForm((f) => ({ ...f, workspaceId: e.target.value.trim().toUpperCase() }))} placeholder={t('admin.slackDest.workspace')} aria-label={t('admin.slackDest.workspace')} />
          <Input value={form.channelId} onChange={(e) => setForm((f) => ({ ...f, channelId: e.target.value.trim().toUpperCase() }))} placeholder={t('admin.slackDest.channel')} aria-label={t('admin.slackDest.channel')} />
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {MILESTONES.map((m) => (
            <label key={m} className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.milestones.includes(m)} onCheckedChange={(v) => setForm((f) => ({ ...f, milestones: v === true ? [...f.milestones, m] : f.milestones.filter((x) => x !== m) }))} />
              {t(`admin.slackDest.milestoneNames.${m}`)}
            </label>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.approvedForRestricted} onCheckedChange={(v) => setForm((f) => ({ ...f, approvedForRestricted: v === true }))} /> {t('admin.slackDest.restricted')}
          </label>
          <Button
            variant="primary"
            size="sm"
            loading={save.isPending}
            disabled={!form.scopeId || !form.workspaceId || !form.channelId || !form.milestones.length}
            onClick={async () => {
              try {
                await save.mutateAsync({ body: { ...form, milestones: form.milestones as (typeof MILESTONES)[number][] } });
                toast.success(t('admin.slackDest.saved'));
              } catch (error) {
                toastError(error);
              }
            }}
          >
            {t('admin.slackDest.add')}
          </Button>
        </div>
      </Card>
      {(list.data?.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-subtle">{t('admin.slackDest.empty')}</p>
      ) : (
        <Card>
          <ul className="divide-y divide-border">
            {list.data!.data.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2 px-4 py-2 text-sm">
                <Badge tone="outline">{labelOf(d.scopeKind, d.scopeId)}</Badge>
                <span className="font-mono text-xs">
                  {d.workspaceId}/{d.channelId}
                </span>
                {d.approvedForRestricted ? <Badge tone="warning">{t('admin.slackDest.restricted')}</Badge> : null}
                <span className="min-w-0 flex-1 truncate text-xs text-fg-subtle">{d.milestones.map((m) => t(`admin.slackDest.milestoneNames.${m}`)).join(', ')}</span>
                <Button size="sm" variant="ghost" onClick={() => void remove.mutateAsync({ params: { id: d.id } }).catch(toastError)}>
                  {t('admin.slackDest.remove')}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}

const CLASSIFICATIONS: EnvironmentClassification[] = ['PRODUCTION', 'STAGING', 'DEVELOPMENT', 'PREVIEW', 'OTHER'];

/**
 * Service mapping approval (Section 9.2): which repositories build a deployable service and which
 * environments count as production. Production coverage only uses human-approved mappings.
 */
export function ServicesPanel() {
  const { t } = useTranslation();
  const services = useApiQuery('listServices', { query: {} }, { retry: false });
  const items = services.data?.data ?? [];
  return (
    <section className="mt-6 flex flex-col gap-3" aria-labelledby="services-title">
      <div>
        <h2 id="services-title" className="text-sm font-semibold">{t('admin.services.title')}</h2>
        <p className="text-xs text-fg-subtle">{t('admin.services.hint')}</p>
      </div>
      <QueryState query={services} empty={items.length === 0 ? <EmptyState icon={<CheckCircle2 />} title={t('admin.services.empty')} /> : undefined}>
        <div className="flex flex-col gap-3">
          {items.map((service) => (
            <ServiceMappingCard key={service.id} service={service} />
          ))}
        </div>
      </QueryState>
    </section>
  );
}

function ServiceMappingCard({ service }: { service: ServiceView }) {
  const { t } = useTranslation();
  const save = useApiMutation('setServiceMapping', { invalidate: ['listServices'] });
  const [repos, setRepos] = useState(service.repositories.map((r) => ({ id: r.repositoryId, fullName: r.fullName, buildPath: r.buildPath ?? '' })));
  const [envs, setEnvs] = useState(service.environments.map((e) => ({ id: e.id, name: e.name, classification: e.classification, targetSlot: e.targetSlot ?? '' })));
  const [picking, setPicking] = useState(false);
  return (
    <Card className="p-4" data-testid="service-card">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{service.name}</h3>
        <span className="text-xs text-fg-subtle">{service.externalId}</span>
        <Badge tone="outline" className="ml-auto">
          {service.basis}
        </Badge>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted uppercase">{t('admin.services.repositories')}</span>
            <Button size="sm" variant="ghost" onClick={() => setPicking(true)}>
              {t('repoPicker.edit')}
            </Button>
          </div>
          {repos.length ? (
            repos.map((r) => (
              <div key={r.id} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm">{r.fullName}</span>
                <Input className="h-7 w-40" value={r.buildPath} placeholder={t('admin.services.buildPath')} aria-label={t('admin.services.buildPathFor', { name: r.fullName })} onChange={(e) => setRepos((list) => list.map((x) => (x.id === r.id ? { ...x, buildPath: e.target.value } : x)))} />
              </div>
            ))
          ) : (
            <span className="text-xs text-fg-subtle">{t('admin.services.noRepositories')}</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-fg-muted uppercase">{t('admin.services.environments')}</span>
          {envs.map((env) => (
            <div key={env.id} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-sm">{env.name}</span>
              <NativeSelect className="h-7 w-36" value={env.classification} aria-label={t('admin.services.classificationFor', { name: env.name })} onChange={(e) => setEnvs((list) => list.map((x) => (x.id === env.id ? { ...x, classification: e.target.value as EnvironmentClassification } : x)))}>
                {CLASSIFICATIONS.map((c) => (
                  <option key={c} value={c}>
                    {t(`admin.services.classes.${c}`)}
                  </option>
                ))}
              </NativeSelect>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          size="sm"
          variant="primary"
          loading={save.isPending}
          onClick={async () => {
            try {
              await save.mutateAsync({
                params: { id: service.id },
                body: {
                  repositories: repos.map((r) => ({ repositoryId: r.id, ...(r.buildPath.trim() ? { buildPath: r.buildPath.trim() } : {}) })),
                  environments: envs.map((e) => ({ environmentId: e.id, classification: e.classification, ...(e.targetSlot.trim() ? { targetSlot: e.targetSlot.trim() } : {}) })),
                },
              });
              toast.success(t('admin.services.approved'));
            } catch (error) {
              toastError(error);
            }
          }}
        >
          {t('admin.services.approve')}
        </Button>
      </div>
      {picking ? (
        <RepositoryDialog
          title={t('admin.services.repositories')}
          description={t('admin.services.hint')}
          initial={repos.map((r) => ({ id: r.id, fullName: r.fullName }))}
          onSave={async (ids) => {
            const known = new Map(repos.map((r) => [r.id, r]));
            const fresh = await api.call('listRepositories', { query: { limit: '200' } });
            const names = new Map((fresh?.data ?? []).map((r) => [r.id, r.fullName]));
            setRepos(ids.map((id) => known.get(id) ?? { id, fullName: names.get(id) ?? id, buildPath: '' }));
          }}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </Card>
  );
}

const DELIVERY_FILTERS = ['DELIVERY_UNKNOWN,FAILED', 'BOUNCED,SUPPRESSED', 'SENT,DELIVERED', ''] as const;

/**
 * Notification delivery inbox (Section 10.2): uncertain and failed emails or Slack posts are listed
 * with their safe error and retried only through an explicit, audited administrator action.
 */
export function DeliveriesPanel() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<(typeof DELIVERY_FILTERS)[number]>('DELIVERY_UNKNOWN,FAILED');
  const list = useApiQuery('adminDeliveries', { query: { ...(filter ? { status: filter } : {}), limit: '50' } }, { refetchInterval: POLL_ACTIVE, retry: false });
  const retry = useApiMutation('retryDelivery', { invalidate: ['adminDeliveries', 'listExceptions'] });
  const [pending, setPending] = useState<string | null>(null);
  const items = list.data?.data ?? [];
  return (
    <section className="mt-6 flex flex-col gap-3" aria-labelledby="deliveries-title">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 id="deliveries-title" className="text-sm font-semibold">{t('admin.deliveries.title')}</h2>
          <p className="text-xs text-fg-subtle">{t('admin.deliveries.hint')}</p>
        </div>
        <NativeSelect value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)} className="w-56" aria-label={t('admin.deliveries.filter')}>
          {DELIVERY_FILTERS.map((f) => (
            <option key={f} value={f}>
              {t(`admin.deliveries.filters.${f ? f.split(',')[0] : 'ALL'}`)}
            </option>
          ))}
        </NativeSelect>
      </div>
      <QueryState query={list} empty={items.length === 0 ? <EmptyState icon={<CheckCircle2 />} title={t('admin.deliveries.empty')} /> : undefined}>
        <Card>
          <ul className="divide-y divide-border">
            {items.map((d) => (
              <li key={d.id} className="flex flex-wrap items-start gap-3 px-4 py-3" data-testid="delivery-row">
                <Badge tone={d.status === 'DELIVERY_UNKNOWN' || d.status === 'FAILED' || d.status === 'BOUNCED' ? 'warning' : 'neutral'}>{d.status}</Badge>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {d.channel} · {d.event} · {d.recipient?.displayName ?? '—'}
                  </p>
                  <p className="text-xs text-fg-subtle">
                    {t('admin.deliveries.attempts', { count: d.attempts })} · <TimeAgo iso={d.updatedAt} />
                    {d.lastSafeError ? ` · ${d.lastSafeError}` : ''}
                  </p>
                </div>
                {d.status === 'DELIVERY_UNKNOWN' || d.status === 'FAILED' ? (
                  <Button size="sm" variant="ghost" onClick={() => setPending(d.id)}>
                    <RotateCcw /> {t('admin.deliveries.retry')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      </QueryState>
      {pending ? (
        <ReasonDialog
          title={t('admin.deliveries.retryTitle')}
          description={t('admin.deliveries.retryHint')}
          confirmLabel={t('admin.deliveries.retry')}
          onConfirm={async (reason) => {
            try {
              await retry.mutateAsync({ params: { id: pending }, body: { reason }, idempotencyKey: newIdempotencyKey() });
              toast.success(t('admin.deliveries.retried'));
            } catch (error) {
              toastError(error);
              throw error;
            }
          }}
          onClose={() => setPending(null)}
        />
      ) : null}
    </section>
  );
}

export function ExceptionsPanel() {
  const { t } = useTranslation();
  const [kind, setKind] = useState('');
  const list = useApiQuery('listExceptions', { query: { status: 'OPEN', ...(kind ? { kind } : {}) } }, { refetchInterval: POLL_ACTIVE, retry: false });
  const resolve = useApiMutation('resolveException', { invalidate: ['listExceptions'] });
  const [pending, setPending] = useState<{ exception: ExceptionView; status: 'RESOLVED' | 'IGNORED' } | null>(null);
  const kinds = ['UNMATCHED_WORK', 'UNCERTAIN_ASSOCIATION', 'UNMAPPED_IDENTITY', 'UNMAPPED_REPOSITORY', 'UNMAPPED_SERVICE', 'CONNECTOR_FAILURE', 'DUPLICATE_CORRECTION', 'WEBHOOK_INTEGRITY', 'DEPLOYMENT_ORDERING', 'DELIVERY_UNKNOWN', 'IMPORT_EXCEPTION', 'ACL_QUARANTINE', 'SCOPE_REVIEW', 'COMPLETION_BLOCKED'];
  const items = list.data?.data ?? [];
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <NativeSelect value={kind} onChange={(e) => setKind(e.target.value)} className="w-64" aria-label={t('admin.exc.filterKind')}>
          <option value="">{t('admin.exc.all')}</option>
          {kinds.map((k) => (
            <option key={k} value={k}>
              {t(`admin.exc.kinds.${k}`)}
            </option>
          ))}
        </NativeSelect>
      </div>
      <QueryState query={list} empty={items.length === 0 ? <EmptyState icon={<CheckCircle2 />} title={t('admin.exc.empty')} /> : undefined}>
        <Card>
          <ul className="divide-y divide-border">
            {items.map((e) => (
              <li key={e.id} className="flex flex-wrap items-start gap-3 px-4 py-3" data-testid="exception-row">
                <Badge tone="warning">{t(`admin.exc.kinds.${e.kind}`)}</Badge>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{e.summary}</p>
                  <p className="text-xs text-fg-subtle">
                    {e.provider ? `${e.provider} · ` : ''}
                    {t('admin.exc.occurrences', { count: e.occurrences })} · <TimeAgo iso={e.updatedAt} />
                  </p>
                </div>
                {e.subjectKind === 'WORK_ITEM' && e.subjectId ? (
                  <a className="text-xs text-accent hover:underline" href={`/work?selected=${e.subjectId}`}>
                    {t('admin.exc.open')}
                  </a>
                ) : null}
                <Button size="sm" variant="ghost" onClick={() => setPending({ exception: e, status: 'RESOLVED' })}>
                  {t('admin.exc.resolve')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setPending({ exception: e, status: 'IGNORED' })}>
                  {t('admin.exc.ignore')}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      </QueryState>
      {pending ? (
        <ReasonDialog
          title={pending.status === 'RESOLVED' ? t('admin.exc.resolve') : t('admin.exc.ignore')}
          description={pending.exception.summary}
          onClose={() => setPending(null)}
          onConfirm={async (resolution) => {
            await resolve.mutateAsync({ params: { id: pending.exception.id }, body: { status: pending.status, resolution } }).catch(toastError);
          }}
        />
      ) : null}
    </div>
  );
}

export function OperationsPanel() {
  const { t } = useTranslation();
  const ops = useApiQuery('getOperationsStatus', {}, { refetchInterval: POLL_ACTIVE, retry: false });
  const dead = useApiQuery('listDeadLetters', {}, { retry: false });
  const replay = useApiMutation('replayDeadLetter', { invalidate: ['listDeadLetters', 'getOperationsStatus'] });
  const [replaying, setReplaying] = useState<{ queue: string; id: string } | null>(null);
  const data = ops.data?.data;
  return (
    <QueryState query={ops}>
      {data ? (
        <div className="flex flex-col gap-4">
          {data.alerts.length ? (
            <ul className="flex flex-col gap-1">
              {data.alerts.map((a) => (
                <li key={`${a.code}:${a.message}`} className="flex items-center gap-2 rounded bg-danger-soft px-3 py-2 text-sm text-danger">
                  <AlertTriangle className="size-4 shrink-0" /> {a.message}
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded bg-success-soft px-3 py-2 text-sm text-success">{t('admin.ops.noAlerts')}</p>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Card className="p-3">
              <p className="text-xs text-fg-subtle">{t('admin.ops.outbox')}</p>
              <p className="text-xl font-semibold">{formatNumber(data.outbox.pending)}</p>
              {data.outbox.oldestPendingSeconds ? <p className="text-xs text-fg-subtle">{formatDuration(data.outbox.oldestPendingSeconds)}</p> : null}
            </Card>
            <Card className="p-3">
              <p className="text-xs text-fg-subtle">{t('admin.ops.worker')}</p>
              <p className={data.worker.healthy ? 'text-sm font-semibold text-success' : 'text-sm font-semibold text-danger'}>{data.worker.healthy ? t('admin.ops.healthy') : t('admin.ops.stale')}</p>
              <p className="text-xs">
                <TimeAgo iso={data.worker.lastHeartbeatAt} />
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-xs text-fg-subtle">{t('admin.ops.aiTokens')}</p>
              <p className="text-xl font-semibold">{formatNumber(data.aiUsageToday.tokens)}</p>
              <p className="text-xs text-fg-subtle">/ {formatNumber(data.aiUsageToday.limit)}</p>
            </Card>
          </div>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-2 text-left text-xs text-fg-subtle">
                <tr>
                  <th className="px-3 py-2">{t('admin.ops.queue')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.ops.queued')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.ops.active')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.ops.oldest')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.ops.dead')}</th>
                </tr>
              </thead>
              <tbody>
                {data.queues.map((q) => (
                  <tr key={q.name} className="border-t border-border">
                    <td className="px-3 py-1.5 font-mono text-xs">{q.name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{q.queued}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{q.active}</td>
                    <td className="px-3 py-1.5 text-right">{q.oldestQueuedSeconds !== null ? formatDuration(q.oldestQueuedSeconds) : '—'}</td>
                    <td className={q.deadLetters ? 'px-3 py-1.5 text-right font-semibold text-danger' : 'px-3 py-1.5 text-right'}>{q.deadLetters}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <Card>
            {(dead.data?.data ?? []).length === 0 ? (
              <p className="px-4 py-3 text-sm text-fg-subtle">{t('admin.ops.deadEmpty')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {dead.data!.data.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center gap-2 px-4 py-2 text-sm">
                    <span className="font-mono text-xs">{d.queue}</span>
                    <span className="min-w-0 flex-1 truncate text-fg-muted">{d.safeError ?? '—'}</span>
                    <span className="text-xs text-fg-subtle">
                      <TimeAgo iso={d.createdAt} />
                    </span>
                    <Button size="sm" variant="ghost" onClick={() => setReplaying({ queue: d.queue, id: d.id })}>
                      <RotateCcw /> {t('admin.ops.replay')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          {replaying ? (
            <ReasonDialog
              title={t('admin.ops.replay')}
              onClose={() => setReplaying(null)}
              onConfirm={async (reason) => {
                await replay.mutateAsync({ body: { ...replaying, reason } }).catch(toastError);
              }}
            />
          ) : null}
        </div>
      ) : null}
    </QueryState>
  );
}
